import React from "react";
import { ButtonComponent } from "../../../services/types/ComponentTypes";

interface ButtonProps {
  config: ButtonComponent;
  onClick?: (actionId: string, buttonConfig?: ButtonComponent) => void;
}

export const Button: React.FC<ButtonProps> = ({ config, onClick }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isActive, setIsActive] = React.useState(false);

  const handleClick = () => {
    if (config.onPressAction && onClick) {
      // Pass button config so action handler can access Settings, ActionType, etc.
      onClick(config.onPressAction, config);
    }
  };

  const getButtonClasses = () => {
    // Check if this is a submit button
    const isSubmitButton =
      config.onPressAction === "save" ||
      (config.name &&
        (config.name.toLowerCase().includes("submit") ||
          config.name.toLowerCase().includes("save")));

    // Check if this is a View/Summary button
    const isViewButton = config.ActionType === "HTML_DRAWER" || config.onPressAction === "html_drawer" ||
      (config.Label || config.name || "").toLowerCase().includes("view summary");

    // Check if this is an icon-only button (Edit, lock, Print, View)
    const isIconOnlyButton =
      config.ActionType === "Edit" || config.onPressAction === "edit" ||
      (config.Label || config.name || "").toLowerCase().includes("edit") ||
      config.onPressAction === "unlock" || config.onPressAction === "lock" ||
      (config.Label || config.name || "").toLowerCase().includes("lock") ||
      config.ActionType === "PDF" || config.onPressAction === "pdf" ||
      (config.Label || config.name || "").toLowerCase().includes("print") ||
      (config.Label || config.name || "").toLowerCase().includes("pdf") ||
      isViewButton;

    // Remove right margin for submit buttons to allow proper centering
    const marginClass = isSubmitButton ? "" : "mr-2";

    // Adjust min-width for icon-only buttons
    const minWidthClass = isIconOnlyButton ? "min-w-[36px] min-h-[36px]" : "min-w-[70px] min-h-[32px]";

    const baseClasses =
      `outline-none flex items-center my-1 justify-center gap-1.5 transition-all duration-200 ${minWidthClass} text-xs font-medium ${marginClass} focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900 active:scale-[0.99]`;

    const widthClasses =
      config.style?.widthMode === "wrap"
        ? "w-auto"
        : config.style?.widthMode === "full"
          ? "w-full"
          : "w-auto";

    const disabledClasses =
      config.disabled || config.loading
        ? "cursor-not-allowed opacity-70"
        : "cursor-pointer";

    const hoverClasses =
      isHovered && !config.disabled && !config.loading
        ? "-translate-y-px shadow-md brightness-[0.98]"
        : "";

    const activeClasses =
      isActive && !config.disabled && !config.loading
        ? "translate-y-0 shadow-sm"
        : "";

    return `${baseClasses} ${widthClasses} ${disabledClasses} ${hoverClasses} ${activeClasses}`;
  };

  const getButtonStyles = (): React.CSSProperties => {
    // Convert var(--primary-green) to theme-aware color
    let backgroundColor = config.style?.backgroundColor || "var(--primary-green)";
    if (backgroundColor === "var(--primary-green)" || backgroundColor.includes("--primary-green")) {
      backgroundColor = "var(--color-primary-500, #35ba5b)";
    }

    const resolvedBorder =
      (config.style as any)?.border ||
      (config.style?.borderWidth
        ? `${config.style.borderWidth}px solid ${config.style.borderColor || backgroundColor}`
        : undefined);

    return {
      padding: config.style?.padding || 6, // Reduced from 12 to 6 for compact buttons
      backgroundColor: backgroundColor,
      color: config.style?.textColor || "#FFFFFF",
      border: resolvedBorder || "none",
      borderRadius: config.style?.borderRadius || 6, // Reduced from 8 to 6
      fontSize: config.style?.fontSize || 12, // Keep config fontSize if provided, otherwise default to 12
      fontWeight: config.style?.fontWeight || "500", // Reduced from 600 to 500
    };
  };

  // Check if this is a PDF/Print button
  const isPDFButton = config.ActionType === "PDF" || config.onPressAction === "pdf" ||
    ((config as any).Settings || (config as any).settings)?.toLowerCase().includes("pdf") ||
    (config.Label || config.name || "").toLowerCase().includes("print") ||
    (config.Label || config.name || "").toLowerCase().includes("pdf");

  // Check if this is an Edit button
  const isEditButton = config.ActionType === "Edit" || config.onPressAction === "edit" ||
    (config.Label || config.name || "").toLowerCase().includes("edit");

  // Check if this is a Delete button
  const isDeleteButton = config.ActionType === "Delete" || config.onPressAction === "delete" ||
    (config.Label || config.name || "").toLowerCase().includes("delete");

  // Check if this is a Lock/Unlock button
  const isLockButton = config.onPressAction === "unlock" || config.onPressAction === "lock" ||
    (config.Label || config.name || "").toLowerCase().includes("lock");

  // Check if this is a View/Summary button
  const isViewButton = config.ActionType === "HTML_DRAWER" || config.onPressAction === "html_drawer" ||
    (config.Label || config.name || "").toLowerCase().includes("view summary");


  // Get icon for button
  const getButtonIcon = () => {
    if (isEditButton) {
      return (
        <svg
          width={18}
          height={18}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    }
    if (isLockButton) {
      return (
        <svg
          width={18}
          height={18}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      );
    }
    if (isPDFButton) {
      return (
        <svg
          width={18}
          height={18}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
      );
    }
    if (isViewButton) {
      return (
        <svg
          width={18}
          height={18}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    }
    return null;
  };

  const buttonIcon = getButtonIcon();
  const showIconOnly = isEditButton || isLockButton || isPDFButton || isViewButton;

  // Get proper tooltip text for icon-only buttons
  const getTooltipText = () => {
    if (config.toolTip) return config.toolTip;

    if (isEditButton) {
      return "Edit";
    }
    if (isLockButton) {
      // Check if it's unlock action to show appropriate tooltip
      if (config.onPressAction === "unlock") {
        return "Unlock";
      }
      return "Lock";
    }
    if (isPDFButton) {
      return "Print";
    }
    if (isViewButton) {
      return "View Summary";
    }
    return config.Label || (config as any).label || config.name || "Button";
  };

  const tooltipText = getTooltipText();
  const ariaLabelText = showIconOnly ? tooltipText : (config.Label || (config as any).label || config.name || "Button");

  return (
    <button
      className={getButtonClasses()}
      style={getButtonStyles()}
      onClick={handleClick}
      disabled={config.disabled || config.loading}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      title={tooltipText}
      aria-label={ariaLabelText}
      {...(isPDFButton && { "data-pdf-button": "true", "data-action-id": config.onPressAction })}
      {...(config.onPressAction === "cancel" || config.onPressAction === "navigate_back" ? { "data-cancel-button": "true" } : {})}
      {...(isEditButton && { "data-edit-button": "true", "data-action-id": config.onPressAction })}
      {...(isDeleteButton && { "data-delete-button": "true", "data-action-id": config.onPressAction })}
    >
      {config.loading && (
        <div className="w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin" />
      )}
      {showIconOnly ? (
        buttonIcon
      ) : (
        config.Label || (config as any).label || config.name || "Button"
      )}
    </button>
  );
};
